// Fetches available booking slots for a given appointment type within a specific date range.
import { skipToken, useQuery } from '@tanstack/react-query'

import type { AppointmentTypeId, ClinicId } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

type UseAvailableSlotsParams = {
  clinicId: ClinicId
  from: string // ISO date string, e.g. '2026-05-24'
  to: string // ISO date string, e.g. '2026-05-31'
  staffId?: string // pass when exactly one vet is filtered
} & ({ appointmentId: string } | { appointmentTypeId: AppointmentTypeId })

/** Fetches available slots for the given appointment type and visible date range. Disabled when any required param is absent. */
export function useAvailableSlots(params: UseAvailableSlotsParams | typeof skipToken) {
  const api = useOsApi()

  return useQuery({
    queryKey:
      params === skipToken
        ? ['available-slots-range', 'disabled']
        : [
            'available-slots-range',
            'appointmentId' in params ? params.appointmentId : params.appointmentTypeId,
            params.clinicId,
            params.from,
            params.to,
            params.staffId ?? null,
          ],
    queryFn:
      params === skipToken
        ? skipToken
        : async () => {
            const response = await api.get('/appointments/available-slots', {
              query: {
                clinicId: params.clinicId,
                from: params.from,
                to: params.to,
                staffId: params.staffId,
                ...('appointmentId' in params
                  ? { appointmentId: params.appointmentId }
                  : { appointmentTypeId: params.appointmentTypeId }),
              },
            })
            if (!response.success) throw new Error(response.error.message)
            return response.data.slots
          },
    staleTime: 60_000,
  })
}
